// src/components/Chatboard/Chatboard.js
import React, { useState, useEffect, useRef } from 'react';
import { Send, Trash2, Edit2, X, Check, MessageSquare, ExternalLink, Search, Lock, CheckCircle, XCircle } from 'lucide-react';
import io from 'socket.io-client';
import './Chatboard.css';

const Chatboard = ({ user, userProfile }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [stats, setStats] = useState(null);
  const [expandedMessageId, setExpandedMessageId] = useState(null);
  
  // 댓글 기능 관련 state
  const [replyTo, setReplyTo] = useState(null); // { messageId, nickname }
  const [threadFilter, setThreadFilter] = useState(null); // { messageId, nickname, clickedMessageId }

  // 폐쇄그룹 쓰레드 관련 state
  const [isPrivateMode, setIsPrivateMode] = useState(false);
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  const socketRef = useRef(null);

  const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
                     || window.innerWidth <= 768;

  // Socket.IO 연결
  useEffect(() => {
    socketRef.current = io(serverUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    // 새 메시지 수신
    // 새 메시지 수신
    socketRef.current.on('chatboard:new-message', (message) => {
      // 폐쇄그룹 메시지인 경우 수신자 확인
      if (message._recipients) {
        // 현재 사용자가 수신자 목록에 있는지 확인
        if (user && message._recipients.includes(user.email)) {
          const { _recipients, ...cleanMessage } = message;
          setMessages(prev => [...prev, cleanMessage]);
          scrollToBottom();
        }
        // 수신자가 아니면 무시
      } else {
        // 일반 메시지는 모두에게 표시
        setMessages(prev => [...prev, message]);
        scrollToBottom();
      }
    });

    // 메시지 수정 수신
    socketRef.current.on('chatboard:message-updated', (updatedMessage) => {
      setMessages(prev => prev.map(msg => 
        msg.id === updatedMessage.id ? updatedMessage : msg
      ));
    });

    // 메시지 삭제 수신
    socketRef.current.on('chatboard:message-deleted', ({ messageId }) => {
      setMessages(prev => prev.filter(msg => {
        // 삭제된 메시지 자체 제거
        if (msg.id === messageId) return false;
        // 삭제된 메시지가 원본이면 그 댓글들도 제거
        if (msg.parent_message_id === messageId) return false;
        return true;
      }));
    });

    // 댓글 승인 수신
    socketRef.current.on('chatboard:comment-approved', (approvedComment) => {
      // 폐쇄그룹 댓글인 경우 수신자 확인
      if (approvedComment._recipients) {
        // 현재 사용자가 수신자 목록에 있는지 확인
        if (user && approvedComment._recipients.includes(user.email)) {
          const { _recipients, ...cleanComment } = approvedComment;
          setMessages(prev => prev.map(msg => 
            msg.id === cleanComment.id ? cleanComment : msg
          ));
        }
        // 수신자가 아니면 무시
      } else {
        // 일반 댓글은 모두에게 표시
        setMessages(prev => prev.map(msg => 
          msg.id === approvedComment.id ? approvedComment : msg
        ));
      }
    });
    
    socketRef.current.on('connect', () => {
      console.log('✅ Chatboard Socket.IO 연결됨');
    });

    socketRef.current.on('disconnect', () => {
      console.log('❌ Chatboard Socket.IO 연결 끊김');
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [serverUrl]);

  // 초기 메시지 로드
  useEffect(() => {
    loadMessages();
    loadStats();
  }, []);

  // 메시지 로드 
  const loadMessages = async (beforeId = null) => {
    try {
      setIsLoading(true);
      
      // userEmail 파라미터 추가
      const userEmailParam = user?.email ? `&userEmail=${encodeURIComponent(user.email)}` : '';
      const url = beforeId 
        ? `${serverUrl}/api/chatboard/messages?limit=50&beforeId=${beforeId}${userEmailParam}`
        : `${serverUrl}/api/chatboard/messages?limit=50${userEmailParam}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        if (beforeId) {
          setMessages(prev => [...data.messages, ...prev]);
        } else {
          setMessages(data.messages);
          setTimeout(scrollToBottom, 100);
        }
        setHasMore(data.hasMore);
      }

    } catch (error) {
      console.error('❌ 메시지 로드 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 통계 로드
  const loadStats = async () => {
    try {
      const response = await fetch(`${serverUrl}/api/chatboard/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStats(data.stats);
        }
      }
    } catch (error) {
      console.error('❌ 통계 로드 실패:', error);
    }
  };

  // 댓글 개수 계산
  const getCommentCount = (messageId) => {
    return messages.filter(msg => msg.parent_message_id === messageId).length;
  };

  // 필터링된 메시지 가져오기
  const getFilteredMessages = () => {
    let filtered = messages;
    
    // 스레드 필터가 있으면 해당 스레드만
    if (threadFilter) {
      filtered = filtered.filter(msg => 
        msg.id === threadFilter.messageId || 
        msg.parent_message_id === threadFilter.messageId
      );
    }
    
    // 폐쇄그룹 댓글 필터링 (클라이언트 측 추가 보안)
    filtered = filtered.filter(msg => {
      // 댓글이 아니면 통과
      if (!msg.parent_message_id) return true;
      
      // 부모 메시지 찾기
      const parentMsg = messages.find(m => m.id === msg.parent_message_id);
      if (!parentMsg) return true;
      
      // 부모가 폐쇄그룹이면 댓글 필터링
      if (parentMsg.is_private_thread) {
        // 본인이 작성한 댓글이면 표시
        if (msg.user_email === user?.email) return true;
        // 쓰레드 소유자면 표시
        if (parentMsg.user_email === user?.email) return true;
        // 승인된 댓글이면 표시 (승인된 참여자)
        if (msg.is_approved === 1) return true;
        // 그 외는 숨김
        return false;
      }
      
      return true;
    });
    
    return filtered;
  };

  // 댓글 쓰기 버튼 핸들러
  const handleReplyClick = (message) => {
    const targetMessageId = message.parent_message_id || message.id;
    const originalMessage = messages.find(m => m.id === targetMessageId);
    
    if (!originalMessage) {
      console.error('원본 메시지를 찾을 수 없습니다.');
      return;
    }
    
    // 댓글에 답할 때는 @멘션 추가
    if (message.parent_message_id) {
      setNewMessage(`@${message.user_nickname} `);
    } else {
      setNewMessage('');
    }
    
    setReplyTo({
      messageId: targetMessageId,
      nickname: originalMessage.user_nickname
    });
    
    // 입력창으로 스크롤 및 포커스
    setTimeout(() => {
      messageInputRef.current?.focus();
      messageInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  // 댓글 취소 핸들러
  const handleCancelReply = () => {
    setReplyTo(null);
    // @멘션이 있으면 제거
    if (newMessage.startsWith('@')) {
      setNewMessage('');
    }
  };

  // 스레드 필터 핸들러
  const handleThreadFilter = (message) => {
    const targetId = message.parent_message_id || message.id;
    const targetMessage = messages.find(m => m.id === targetId);
    
    if (!targetMessage) {
      console.error('원본 메시지를 찾을 수 없습니다.');
      return;
    }
    
    if (threadFilter?.messageId === targetId) {
      // 이미 필터링 중이면 해제
      setThreadFilter(null);
    } else {
      // 새로운 스레드 필터링
      setThreadFilter({
        messageId: targetId,
        nickname: targetMessage.user_nickname,
        clickedMessageId: message.id
      });
      
      // 원본 메시지로 스크롤
      setTimeout(() => {
        const messageElement = document.querySelector(`[data-message-id="${targetId}"]`);
        if (messageElement) {
          messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  // 메시지 전송 
  const handleSendMessage = async (e, sendAsPrivate = false) => {
    e.preventDefault();

    if (!user || !userProfile?.nickname) {
      alert('로그인이 필요합니다.');
      return;
    }

    if (!newMessage.trim()) {
      return;
    }

    if (newMessage.length > 500) {
      alert('메시지는 500자를 초과할 수 없습니다.');
      return;
    }

    try {
      setIsSending(true);
      
      const requestBody = {
        userEmail: user.email,
        userNickname: userProfile.nickname,
        userPhotoUrl: userProfile.photoURL || null,
        message: newMessage,
        parentMessageId: replyTo?.messageId || threadFilter?.messageId || null,
        parentNickname: replyTo?.nickname || threadFilter?.nickname || null,
        isPrivateThread: sendAsPrivate || isPrivateMode
      };

      const response = await fetch(`${serverUrl}/api/chatboard/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '메시지 전송에 실패했습니다.');
      }

      const data = await response.json();

      if (data.success) {
        setNewMessage('');
        setReplyTo(null);
        setIsPrivateMode(false);
        await loadStats();
        
        if (isMobile) {
          setTimeout(scrollToBottom, 100);
        }
      }

    } catch (error) {
      console.error('❌ 메시지 전송 실패:', error);
      alert(error.message || '메시지 전송에 실패했습니다.');
    } finally {
      setIsSending(false);
    }
  };

  // 메시지 수정 가능 여부 체크 (5분 이내)
  const canDeleteMessage = (message) => {
    // 관리자는 항상 삭제 가능
    if (isAdmin) return true;
    
    // 쓰레드 소유자는 해당 쓰레드의 모든 댓글 삭제 가능
    if (isThreadOwner(message) && message.parent_message_id) {
      return true;
    }
    
    // 본인 메시지는 5분 이내 삭제 가능
    const createdTime = new Date(message.created_at).getTime();
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    return (now - createdTime) <= fiveMinutes;
  };

  const canEditMessage = (message) => {
    const createdAt = new Date(message.created_at); 
    const now = new Date(); 
    const diffMinutes = (now - createdAt) / 1000 / 60; 
    return diffMinutes <= 5;
  };

  // 메시지 수정 시작
  const startEdit = (message) => {
    if (!canEditMessage(message)) {
      alert('메시지는 작성 후 5분 이내에만 수정할 수 있습니다.');
      return;
    }
    setEditingMessageId(message.id);
    setEditingText(message.message);
  };

  // 메시지 수정 취소
  const cancelEdit = () => {
    setEditingMessageId(null);
    setEditingText('');
  };

  // 메시지 수정 저장
  const saveEdit = async (messageId) => {
    if (!editingText.trim()) {
      alert('메시지 내용이 비어있습니다.');
      return;
    }

    if (editingText.length > 500) {
      alert('메시지는 500자를 초과할 수 없습니다.');
      return;
    }

    try {
      const response = await fetch(`${serverUrl}/api/chatboard/messages/${messageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          userEmail: user.email,
          message: editingText.trim()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setEditingMessageId(null);
        setEditingText('');
        // Socket.IO가 자동으로 메시지를 업데이트하므로 여기서는 따로 업데이트하지 않음
      } else {
        alert(data.error || '메시지 수정에 실패했습니다.');
      }

    } catch (error) {
      console.error('❌ 메시지 수정 실패:', error);
      alert('메시지 수정 중 오류가 발생했습니다.');
    }
  };

  // 메시지 삭제
  const handleDelete = async (messageId) => {
    if (!window.confirm('정말로 이 메시지를 삭제하시겠습니까?')) {
      return;
    }

    try {
      const response = await fetch(`${serverUrl}/api/chatboard/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          userEmail: user.email
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        alert(data.error || '메시지 삭제에 실패했습니다.');
      }
      // Socket.IO가 자동으로 메시지를 삭제하므로 여기서는 따로 처리하지 않음

    } catch (error) {
      console.error('❌ 메시지 삭제 실패:', error);
      alert('메시지 삭제 중 오류가 발생했습니다.');
    }
  };

  // 댓글 승인
  const handleApprove = async (messageId) => {
    if (!window.confirm('이 댓글을 승인하시겠습니까?')) {
      return;
    }

    try {
      const response = await fetch(`${serverUrl}/api/chatboard/messages/${messageId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          userEmail: user.email
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '승인에 실패했습니다.');
      }

      const data = await response.json();
      if (data.success) {
        console.log('✅ 댓글 승인 성공');
        // Socket.IO를 통해 실시간 업데이트됨
      }

    } catch (error) {
      console.error('❌ 댓글 승인 실패:', error);
      alert(error.message || '댓글 승인에 실패했습니다.');
    }
  };

  // 댓글 거절
  const handleReject = async (messageId) => {
    if (!window.confirm('이 댓글을 거절하시겠습니까? 거절된 댓글은 삭제됩니다.')) {
      return;
    }

    try {
      const response = await fetch(`${serverUrl}/api/chatboard/messages/${messageId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          userEmail: user.email
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '거절에 실패했습니다.');
      }

      const data = await response.json();
      if (data.success) {
        console.log('✅ 댓글 거절 성공');
        // Socket.IO를 통해 실시간 업데이트됨
      }

    } catch (error) {
      console.error('❌ 댓글 거절 실패:', error);
      alert(error.message || '댓글 거절에 실패했습니다.');
    }
  };

  // 폐쇄그룹 쓰레드 소유자 확인
  const isThreadOwner = (message) => {
    if (!user) return false;
    
    // 폐쇄그룹 쓰레드 본인이면 true
    if (message.is_private_thread === 1 && message.user_email === user.email) {
      return true;
    }
    
    // 폐쇄그룹 쓰레드의 댓글인 경우, 부모 메시지의 소유자 확인
    if (message.parent_message_id) {
      const parentMessage = messages.find(m => m.id === message.parent_message_id);
      if (parentMessage && parentMessage.is_private_thread === 1 && parentMessage.user_email === user.email) {
        return true;
      }
    }
    
    return false;
  };

  // 승인 대기 중인지 확인
  const isPendingApproval = (message) => {
    return message.is_approved === 0;
  };

  // 비밀채팅 토글
  const handlePrivateToggle = () => {
    if (replyTo) {
      alert('댓글 작성 중에는 비밀채팅을 사용할 수 없습니다.');
      return;
    }
    setIsPrivateMode(!isPrivateMode);
  };

  // 스크롤 하단으로 이동
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      setTimeout(() => {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }, 100);
    }
  };

  // 더 많은 메시지 로드
  const loadMoreMessages = () => {
    if (messages.length > 0 && !isLoading) {
      const oldestMessageId = messages[0].id;
      loadMessages(oldestMessageId);
    }
  };

  // 메시지 소유자 확인
  const isMessageOwner = (message) => {
    return user?.email === message.user_email;
  };

  // 관리자 확인
  const isAdmin = ['gategil1149@gmail.com', 'worldlandcafe@gmail.com'].includes(user?.email);

  // 시간 포맷팅
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return '방금 전';
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;

    return date.toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 메시지 요약 (한 줄로)
  const getSummaryText = (text) => {
    const maxLength = 90;
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // 메시지 토글
  const toggleMessage = (messageId) => {
    setExpandedMessageId(expandedMessageId === messageId ? null : messageId);
  };

  // 팝업 창 열기
  const openChatboardPopup = () => {
    // 모바일 디바이스 감지
    
    
    let width, height, left, top, features;
    
    if (isMobile) {
      // 모바일: 화면 전체 크기
      width = window.screen.width;
      height = window.screen.height;
      features = `width=${width},height=${height},fullscreen=yes,resizable=no,scrollbars=yes`;
    } else {
      // 데스크톱: 넓은 크기 (480x800)
      width = 480;
      height = 800;
      left = (window.screen.width - width) / 2;
      top = (window.screen.height - height) / 2;
      features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
    }
    
    window.open('/chatboard', 'ChatboardPopup', features);
  };

  return (
    <div className="chatboard-container">
      <div className="chatboard-header">
        <div className="chatboard-title">
          <MessageSquare className="title-icon" />
          <h3>실시간 소통 게시판 (방명록/채팅)</h3>
        </div>
        <div className="chatboard-header-right">
          {stats && (
            <div className="chatboard-stats">
              <span className="stat-item">
                💬 {stats.total_messages}개 메시지
              </span>
              <span className="stat-item">
                👥 {stats.total_users}명 참여
              </span>
            </div>
          )}
          <button 
            className="popup-btn"
            onClick={openChatboardPopup}
            title="새 창으로 열기"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
              <polyline points="15 3 21 3 21 9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
          </button>
        </div>
      </div>

      {/* 필터 바 */}
      {/* {user && userProfile?.nickname && (
        <div className="filter-bar">
          {threadFilter ? (
            <div className="filter-active">
              <span>필터: {threadFilter.nickname}의 스레드</span>
              <button onClick={() => setThreadFilter(null)}>✕</button>
            </div>
          ) : (
            <div className="filter-inactive">
              <span>전체 보기</span>
            </div>
          )}
        </div>
      )} */}

      <div className="chatboard-messages" ref={messagesContainerRef}>
        {!user || !userProfile?.nickname ? (
          <div className="chatboard-login-prompt">
            <img 
              src={isMobile ? "/images/welcomeToChat1.png" : "/images/welcomeToChat2.png"} 
              alt="Log in to join the chat and share your thoughts!" 
              className="login-prompt-image"
            />
          </div>
        ) : (
          <>
            {hasMore && messages.length > 0 && !threadFilter && (
              <button 
                className="load-more-btn"
                onClick={loadMoreMessages}
                disabled={isLoading}
              >
                {isLoading ? '로딩 중...' : '이전 메시지 보기'}
              </button>
            )}

            {isLoading && messages.length === 0 && (
              <div className="chatboard-loading">
                <div className="spinner"></div>
                <p>메시지를 불러오는 중...</p>
              </div>
            )}

            {!isLoading && getFilteredMessages().length === 0 && (
              <div className="chatboard-empty">
                <MessageSquare className="empty-icon" />
                <p>아직 메시지가 없습니다.</p>
                <p className="empty-subtitle">첫 번째 메시지를 남겨보세요!</p>
              </div>
            )}

            {getFilteredMessages().map((message) => {
              const isComment = message.parent_message_id !== null;
              const isOwn = isMessageOwner(message);
              const isFiltered = threadFilter?.messageId === (message.parent_message_id || message.id);
              const isClicked = threadFilter?.clickedMessageId === message.id;
              const commentCount = getCommentCount(message.id);
              const isPrivateThread = message.is_private_thread === 1;
              const needsApproval = isPendingApproval(message);
              const canApprove = isThreadOwner(message) && needsApproval;

              return (
                <div 
                  key={message.id}
                  data-message-id={message.id}
                  className={`message-item 
                    ${isComment ? 'comment-message' : ''} 
                    ${isOwn ? 'own-message' : ''}
                    ${isFiltered && !isComment ? 'filtered-origin' : ''}
                    ${isClicked ? 'clicked-message' : ''}
                    ${needsApproval ? 'pending-approval' : ''}`}
                > 
                  {/* 댓글 표시 */}
                  {isComment && (
                    <div className="reply-indicator">
                      ↳ To: {message.parent_nickname}
                    </div>
                  )}

                  <div className="message-avatar">
                    {message.user_photo_url ? (
                      <img src={message.user_photo_url} alt={message.user_nickname} />
                    ) : (
                      <div className="avatar-placeholder">
                        {message.user_nickname.charAt(0).toUpperCase()}
                      </div>
                    )} 
                    <span className="message-nickname">{message.user_nickname}</span> 
                  </div>

                  <div className="message-content">
                    {editingMessageId === message.id ? (
                      <div className="message-edit-form">
                        <textarea
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          maxLength={500}
                          rows={1}
                          autoFocus
                        />
                        <div className="edit-actions">
                          <button 
                            className="btn-save"
                            onClick={() => saveEdit(message.id)}
                          >
                            <Check size={16} />
                            저장
                          </button>
                          <button 
                            className="btn-cancel"
                            onClick={cancelEdit}
                          >
                            <X size={16} />
                            취소
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div 
                          className={`message-text ${expandedMessageId === message.id ? 'expanded' : 'collapsed'}`}
                          onClick={() => toggleMessage(message.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          {expandedMessageId === message.id 
                            ? message.message 
                            : getSummaryText(message.message)
                          }
                        </div>
                        <div className="message-header"> 
                          <span className="message-time">{formatTime(message.created_at)}</span>
                          
                          {/* 폐쇄그룹 쓰레드 표시 */}
                          {isPrivateThread && !isComment && (
                            <span className="private-thread-badge" title="폐쇄그룹 쓰레드">
                              <Lock size={12} /> 비밀
                            </span>
                          )}
                          
                          {/* 승인 대기 표시 */}
                          {needsApproval && (
                            <span className="pending-badge" title="승인 대기 중">
                              ⏳ 승인대기
                            </span>
                          )}
                          
                          {/* 댓글 개수 (원본 메시지만) */}
                          {!isComment && commentCount > 0 && (
                            <span className="comment-badge">💬 {commentCount}</span>
                          )}

                          {/* 액션 버튼들 */}
                          <div className="message-actions">
                            {/* 승인/거절 버튼 (쓰레드 소유자에게만) */}
                            {canApprove && (
                              <>
                                <button 
                                  className="btn-approve"
                                  onClick={() => handleApprove(message.id)}
                                  title="승인"
                                >
                                  <CheckCircle size={14} />
                                </button>
                                <button 
                                  className="btn-reject"
                                  onClick={() => handleReject(message.id)}
                                  title="거절"
                                >
                                  <XCircle size={14} />
                                </button>
                              </>
                            )}
                            
                            {/* 수정/삭제 버튼 */}
                            {(isOwn || isAdmin || (isThreadOwner(message) && isComment)) && (
                              <>
                                {isOwn && canEditMessage(message) && (
                                  <button 
                                    className="btn-edit"
                                    onClick={() => startEdit(message)}
                                    title={canEditMessage(message) ? "수정" : "작성 후 5분 이내에만 수정 가능"}
                                    disabled={!canEditMessage(message)}
                                  >
                                    <Edit2 size={14} />
                                  </button>
                                )}
                                {canDeleteMessage(message) && (
                                <button 
                                  className="btn-delete"
                                  onClick={() => handleDelete(message.id)}
                                  title="삭제"
                                >
                                  <Trash2 size={14} />
                                </button>
                                )}
                              </>
                            )}
                            
                            {/* 댓글 버튼 */}
                            {!needsApproval && (
                              <button 
                                className="btn-reply"
                                onClick={() => handleReplyClick(message)}
                                title="댓글 달기"
                              >
                                💬
                              </button>
                            )}
                            
                            {/* 스레드 보기 버튼 */}
                            <button 
                              className="btn-thread"
                              onClick={() => handleThreadFilter(message)}
                              title="이 스레드만 보기"
                            >
                              <Search size={14} />
                            </button>
                          </div>
                          
                          {message.updated_at !== message.created_at && (
                            <span className="message-edited">(수정됨)</span>
                          )}
                        </div>
                        
                        

                        
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            <div ref={messagesEndRef} />
          </>
        )}
      </div> 

      {user && userProfile?.nickname ? (
        <form className="chatboard-input-form" onSubmit={handleSendMessage}>
          {/* 댓글 헤더 */}
          {replyTo && (
            <div className="reply-header">
              <span>To: {replyTo.nickname}</span>
              <button type="button" onClick={handleCancelReply}>✕</button>
            </div>
          )}

          <div className="input-wrapper">
            <textarea
              ref={messageInputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={replyTo 
                ? `${replyTo.nickname}에게 댓글을 입력하세요...` 
                : "메시지를 입력하세요...(500자 이내). 마우스로 메시지를 클릭하면 전체 내용을 보실 수 있어요."}
              maxLength={500}
              rows={2}
              disabled={isSending}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
            />
            <div className="input-footer">
              <span className="char-count">
                {newMessage.length} / 500
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                {/* 비밀채팅 버튼 (댓글이 아닐 때만) */}
                {!replyTo && (
                  <button 
                    type="button"
                    className={`btn-private ${isPrivateMode ? 'active' : ''}`}
                    onClick={handlePrivateToggle}
                    disabled={isSending}
                    title="비밀채팅 모드"
                  >
                    <Lock size={16} />
                    {isPrivateMode && <span>비밀</span>}
                  </button>
                )}
                
                <button 
                  type="submit" 
                  className="btn-send"
                  disabled={!newMessage.trim() || isSending}
                >
                  {isSending ? (
                    <div className="spinner-small"></div>
                  ) : (
                    <>
                      <Send size={16} />
                      <span>전송</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="chatboard-login-required">
          <p>게시판을 조회하거나 메시지를 작성하시려면 로그인이 필요합니다.</p>
        </div>
      )}
    </div>
  );
};

export default Chatboard;